import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { releaseApi } from '../api'
import type { BackgroundJobsCard, BackgroundJobsPlane } from '../api'
import { PageDescription } from '../components/PageDescription'

function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)
    return (
        <button
            onClick={() => {
                void navigator.clipboard.writeText(text).then(() => {
                    setCopied(true)
                    setTimeout(() => setCopied(false), 1500)
                })
            }}
            className="shrink-0 rounded-md border border-slate-700 px-2 py-1 text-[10px] font-medium text-slate-400 hover:bg-slate-800 hover:text-slate-200 transition-colors"
            title="Copy to clipboard"
        >
            {copied ? 'Copied' : 'Copy'}
        </button>
    )
}

function EnvironmentCard({ card }: { card: BackgroundJobsCard }) {
    return (
        <div className="rounded-lg border border-slate-700 bg-slate-800/40 p-3.5">
            <div className="flex items-center justify-between mb-2.5">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-300 font-mono">
                    {card.environment}
                </span>
                {card.dashboardUrl ? (
                    <a
                        href={card.dashboardUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-md bg-indigo-500/15 px-2.5 py-1 text-[11px] font-medium text-indigo-300 ring-1 ring-indigo-500/30 hover:bg-indigo-500/25 transition-colors"
                    >
                        Open dashboard
                        <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M6 3h7v7M13 3L7 9M11 9v4H3V5h4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </a>
                ) : (
                    <span className="rounded-md bg-slate-700/40 px-2 py-1 text-[10px] font-medium text-slate-500 ring-1 ring-slate-600/40">
                        no ingress URL
                    </span>
                )}
            </div>

            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Port-forward fallback</div>
            <div className="flex items-start gap-2">
                <code className="flex-1 min-w-0 block rounded-md bg-slate-950/70 border border-slate-800 px-2.5 py-2 text-[11px] leading-relaxed text-slate-300 font-mono break-all">
                    {card.portForwardCommand}
                </code>
                <CopyButton text={card.portForwardCommand} />
            </div>
        </div>
    )
}

function PlaneColumn({ plane }: { plane: BackgroundJobsPlane }) {
    return (
        <div className="rounded-xl border border-slate-700 bg-slate-800/30 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-700/50 bg-slate-800/60">
                <span className="text-base font-semibold text-white">{plane.displayName}</span>
                <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono">{plane.id}</span>
            </div>
            <div className="p-4 flex flex-col gap-3">
                {plane.environments.map(card => (
                    <EnvironmentCard key={card.environment} card={card} />
                ))}
            </div>
        </div>
    )
}

export function BackgroundJobsPage() {
    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['background-jobs'],
        queryFn: releaseApi.getBackgroundJobs,
        staleTime: 5 * 60_000,
    })

    return (
        <>
            <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center">
                    <div>
                        <span className="text-sm font-semibold text-slate-200">Background Jobs</span>
                        <PageDescription
                            subtitle="Hangfire dashboards per plane × environment"
                            storageKey="desc-background-jobs"
                            detail="Deep links to each plane's Hangfire dashboard (mapped at /internal/hangfire). The two planes have SEPARATE Hangfire storages and disjoint job sets, so they are two independent dashboards, not one. Every dashboard is gated by an operator token — append ?token=<DashboardToken> once to set the auth cookie. The tenant-plane dashboard is Coravant-ops-only (cross-tenant, exposes args + stack traces + destructive controls) and deliberately not publicly routed, so use the port-forward fallback for it."
                        />
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 w-full">
                {isLoading && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {[...Array(2)].map((_, i) => (
                            <div key={i} className="h-80 rounded-xl border border-slate-700 bg-slate-800/40 animate-pulse" />
                        ))}
                    </div>
                )}

                {isError && (
                    <div className="rounded-xl border border-red-800 bg-red-950/50 p-6 text-red-300 text-sm">
                        Failed to load background-jobs config:{' '}
                        {error instanceof Error ? error.message : 'Unknown error'}
                    </div>
                )}

                {data && (
                    <>
                        <div className="mb-5 rounded-xl border border-amber-800/50 bg-amber-950/30 px-4 py-3 text-xs text-amber-200/90 leading-relaxed">
                            <span className="font-semibold">Operator token required.</span> {data.tokenNote}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            {data.planes.map(plane => (
                                <PlaneColumn key={plane.id} plane={plane} />
                            ))}
                        </div>
                    </>
                )}
            </main>
        </>
    )
}
